if (!customElements.get('collection-variant-selector')) {
  /**
   * Collection Variant Selector
   * Optimiert für Performance:
   * - Request Batching: Kombiniert mehrere parent-Tags in einem GraphQL Request
   * - sessionStorage Cache: Seitenübergreifend, 30 Min TTL
   * - Concurrency Limit: Max 2 parallele Requests
   * - IntersectionObserver: Lädt nur sichtbare Elemente
   * - Debouncing: Sammelt Requests bevor sie abgeschickt werden
   */

  // Global request queue and cache
  const CVS_CONFIG = {
    CACHE_TTL: 30 * 60 * 1000, // 30 Minuten
    CACHE_PREFIX: 'cvs:v2:',
    MAX_CONCURRENT: 2,
    BATCH_DELAY: 50, // ms - Zeit zum Sammeln von Requests
    MAX_PARENTS_PER_REQUEST: 10 // Shopify Query-Limit beachten
  };

  // Shared state
  const pendingElements = new Map(); // parentTag -> Set of elements waiting for data
  let batchTimeout = null;
  let activeRequests = 0;
  const requestQueue = [];

  // Session Storage Cache
  function getCacheKey(parentTag) {
    const lang = window.ISO_CODE || 'DE';
    return `${CVS_CONFIG.CACHE_PREFIX}${lang}:${parentTag}`;
  }

  function readCache(parentTag) {
    try {
      const key = getCacheKey(parentTag);
      const cached = sessionStorage.getItem(key);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp > CVS_CONFIG.CACHE_TTL) {
        sessionStorage.removeItem(key);
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  function writeCache(parentTag, data) {
    try {
      const key = getCacheKey(parentTag);
      sessionStorage.setItem(key, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (e) {
      // Storage full - clear old entries
      clearOldCache();
    }
  }

  function clearOldCache() {
    try {
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(CVS_CONFIG.CACHE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      // Remove oldest half
      keysToRemove.slice(0, Math.ceil(keysToRemove.length / 2)).forEach(k => {
        sessionStorage.removeItem(k);
      });
    } catch (e) {}
  }

  // Batched fetch logic
  function scheduleBatch() {
    if (batchTimeout) return;

    batchTimeout = setTimeout(() => {
      batchTimeout = null;
      processBatch();
    }, CVS_CONFIG.BATCH_DELAY);
  }

  function processBatch() {
    if (pendingElements.size === 0) return;

    // Collect parent tags that need fetching
    const parentTags = Array.from(pendingElements.keys());

    // Check cache first, serve cached immediately
    const uncachedTags = [];
    parentTags.forEach(tag => {
      const cached = readCache(tag);
      if (cached) {
        // Serve from cache immediately
        const elements = pendingElements.get(tag);
        elements?.forEach(el => el.renderVariants(cached));
        pendingElements.delete(tag);
      } else {
        uncachedTags.push(tag);
      }
    });

    if (uncachedTags.length === 0) return;

    // Split into batches
    for (let i = 0; i < uncachedTags.length; i += CVS_CONFIG.MAX_PARENTS_PER_REQUEST) {
      const batch = uncachedTags.slice(i, i + CVS_CONFIG.MAX_PARENTS_PER_REQUEST);
      requestQueue.push(batch);
    }

    processQueue();
  }

  function processQueue() {
    while (activeRequests < CVS_CONFIG.MAX_CONCURRENT && requestQueue.length > 0) {
      const batch = requestQueue.shift();
      activeRequests++;

      fetchBatch(batch)
        .then(results => {
          // Distribute results to waiting elements
          results.forEach(({ parentTag, products }) => {
            writeCache(parentTag, products);
            const elements = pendingElements.get(parentTag);
            elements?.forEach(el => el.renderVariants(products));
            pendingElements.delete(parentTag);
          });
        })
        .catch(err => {
          // Silent fail - remove from pending
          batch.forEach(tag => pendingElements.delete(tag));
        })
        .finally(() => {
          activeRequests--;
          processQueue(); // Process next in queue
        });
    }
  }

  async function fetchBatch(parentTags) {
    // Build OR query for multiple parents
    const queryParts = parentTags.map(tag => `tag:"parent:${tag}"`);
    const combinedQuery = queryParts.join(' OR ');

    const response = await fetch("/api/2024-10/graphql.json", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": "573612d745d5f109fa297da9764819e0",
      },
      body: JSON.stringify({
        variables: {
          first: 250, // Max to get all variants for multiple parents
          query: combinedQuery
        },
        query: `
          query getProducts($first: Int, $query: String) @inContext(language: ${window.ISO_CODE || 'DE'}) {
            products(first: $first, query: $query) {
              edges {
                node {
                  id
                  title
                  handle
                  tags
                  inventory_quantity: totalInventory
                  featuredImage {
                    url
                  }
                  search_color_metafield: metafield(namespace: "custom", key: "suchfarbe") {
                    value
                  }
                  color_metafield: metafield(namespace: "custom", key: "color") {
                    value
                  }
                }
              }
            }
          }
        `,
      })
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    const data = await response.json();
    const edges = data?.data?.products?.edges || [];

    // Group products by their parent tag
    const productsByParent = new Map();
    parentTags.forEach(tag => productsByParent.set(tag, []));

    edges.forEach(({ node }) => {
      const parentTag = node.tags?.find(t => t.startsWith('parent:'))?.replace('parent:', '');
      if (parentTag && productsByParent.has(parentTag)) {
        if (node.inventory_quantity > 0) {
          productsByParent.get(parentTag).push({
            handle: node.handle,
            title: node.title,
            featuredImage: node.featuredImage ? { url: convertImageUrl(node.featuredImage.url) } : null,
            inventory_quantity: node.inventory_quantity,
            search_color: node.search_color_metafield?.value,
            color: node.color_metafield?.value,
          });
        }
      }
    });

    return parentTags.map(parentTag => ({
      parentTag,
      products: productsByParent.get(parentTag) || []
    }));
  }

  function convertImageUrl(url) {
    if (!url) return url;
    return url.replace(/https:\/\/cdn\.shopify\.com\/s\/files\/1\/(\d+\/)+/, window.location.origin + '/cdn/shop/');
  }

  class CollectionVariantSelector extends HTMLElement {
    constructor() {
      super();
      this.observer = null;
    }

    connectedCallback() {
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.queueLoad();
                this.observer.disconnect();
              }
            });
          },
          { rootMargin: '100px' }
        );
        this.observer.observe(this);
      } else {
        this.queueLoad();
      }
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
      // Remove from pending if disconnected before load
      const parentTag = this.getAttribute("data-parent");
      if (parentTag && pendingElements.has(parentTag)) {
        pendingElements.get(parentTag).delete(this);
      }
    }

    queueLoad() {
      const parentTag = this.getAttribute("data-parent");
      if (!parentTag) return;

      // Check cache first - immediate render
      const cached = readCache(parentTag);
      if (cached) {
        this.renderVariants(cached);
        return;
      }

      // Add to pending elements
      if (!pendingElements.has(parentTag)) {
        pendingElements.set(parentTag, new Set());
      }
      pendingElements.get(parentTag).add(this);

      // Schedule batch processing
      scheduleBatch();
    }

    renderVariants(relatedProducts) {
      if (!relatedProducts || relatedProducts.length === 0) return;

      const colorsFragment = document.createDocumentFragment();
      const imagesFragment = document.createDocumentFragment();

      const productCard = this.closest('product-card');
      const mainImage = productCard?.querySelector('.product-primary-image');
      let originalSrc = null;
      let originalSrcset = null;

      if (mainImage) {
        originalSrc = mainImage.src;
        originalSrcset = mainImage.srcset;
      }

      // Group by unique search_color
      const uniqueSearchColors = new Map();
      relatedProducts.forEach(p => {
        if (p.search_color && !uniqueSearchColors.has(p.search_color)) {
          uniqueSearchColors.set(p.search_color, p);
        }
      });
      const colorProducts = Array.from(uniqueSearchColors.values());
      const totalProductCount = relatedProducts.length;

      // Render color dots (mobile)
      let dotsRendered = 0;
      colorProducts.forEach((p) => {
        if (dotsRendered < 4 && window.themeSwatch?.[p.search_color]) {
          const colorSpan = document.createElement('span');
          colorSpan.className = 'product-sibling-color';
          colorSpan.style.backgroundColor = window.themeSwatch[p.search_color];
          colorSpan.title = p.color || p.search_color;
          colorsFragment.appendChild(colorSpan);
          dotsRendered++;
        }
      });

      if (totalProductCount > 4) {
        const plusSpan = document.createElement('span');
        plusSpan.className = 'product-sibling-color-plus';
        plusSpan.textContent = `+${totalProductCount - 4}`;
        colorsFragment.appendChild(plusSpan);
      }

      // Render product images (desktop)
      relatedProducts.forEach((p, i) => {
        if (i < 4 && p.featuredImage?.url) {
          const smallImageUrl = p.featuredImage.url.split('?')[0] + '?width=200&height=200&format=webp';
          const largeImageUrl = p.featuredImage.url.split('?')[0] + '?width=560&height=560&format=webp';

          const link = document.createElement('a');
          link.href = `/products/${p.handle}`;
          link.title = p.title;
          link.className = 'product-sibling sibling-link';
          link.dataset.color = p.color;
          link.dataset.largeImage = largeImageUrl;

          const img = document.createElement('img');
          img.src = smallImageUrl;
          img.width = 50;
          img.height = 50;
          img.alt = p.title;
          img.className = 'sibling-image';
          img.loading = 'lazy';

          link.appendChild(img);
          imagesFragment.appendChild(link);

          if (mainImage) {
            link.addEventListener('mouseenter', () => {
              mainImage.src = largeImageUrl;
              mainImage.srcset = largeImageUrl;
            });
            link.addEventListener('mouseleave', () => {
              mainImage.src = originalSrc;
              mainImage.srcset = originalSrcset;
            });
          }
        } else if (i === 4) {
          const plusSpan = document.createElement('span');
          plusSpan.className = 'product-sibling-plus';
          plusSpan.textContent = `+${relatedProducts.length - 4}`;
          imagesFragment.appendChild(plusSpan);
        }
      });

      const colorsContainer = this.querySelector(".product-sibling-colors");
      const imagesContainer = this.querySelector(".product-sibling-images");

      if (colorsContainer) colorsContainer.appendChild(colorsFragment);
      if (imagesContainer) imagesContainer.appendChild(imagesFragment);
    }
  }

  customElements.define('collection-variant-selector', CollectionVariantSelector);
}
